import React, { useState, useMemo } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Scale from 'lucide-react/dist/esm/icons/scale';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import Search from 'lucide-react/dist/esm/icons/search';
import { getTranslation } from '../../../i18n';

/**
 * Analysis Selection Card
 */
const AnalysisCard = ({ analysis, isSelected, onSelect, selectionNumber, disabled, t }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <button
      onClick={() => onSelect(analysis)}
      disabled={disabled}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
        isSelected
          ? 'bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border-violet-500/50 shadow-lg shadow-violet-500/10'
          : disabled
          ? 'bg-slate-800/20 border-subtle opacity-50 cursor-not-allowed'
          : 'bg-surface-primary border-subtle hover:border-strong hover:bg-surface-secondary'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 size={14} className={isSelected ? 'text-violet-400' : 'text-slate-500'} />
            <span className={`text-sm font-semibold truncate ${isSelected ? 'text-slate-100' : 'text-slate-300'}`}>
              {analysis.company_name || t('unknownCompany')}
            </span>
            {analysis.fiscal_years_display && (
              <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                isSelected 
                  ? 'bg-violet-500/30 text-violet-200' 
                  : 'bg-brand/20 text-primary'
              }`}>
                {analysis.fiscal_years_display}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <FileText size={12} className="text-slate-600" />
            <span className="text-xs text-slate-500 truncate">{analysis.document_name}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Calendar size={12} className="text-slate-600" />
            <span className="text-xs text-slate-500">{formatDate(analysis.completed_at || analysis.created_at)}</span>
          </div>
        </div>
        
        {/* Selection indicator */}
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-gradient-to-r from-violet-500 to-cyan-500 text-content-inverse'
            : 'bg-slate-700/50 text-slate-500 border border-subtle'
        }`}>
          {isSelected ? (
            <span className="text-xs font-bold">{selectionNumber}</span>
          ) : (
            <span className="text-xs">+</span>
          )}
        </div>
      </div>
    </button>
  );
};

/**
 * Comparison Selector Modal
 */
const ComparisonSelector = ({ 
  analyses, 
  onCompare, 
  onClose, 
  isLoading = false,
  language = 'en'
}) => {
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const t = (key, variables = {}) => getTranslation(language, key, variables);

  // Filter analyses based on search (includes fiscal years)
  const filteredAnalyses = useMemo(() => {
    if (!searchQuery.trim()) return analyses;
    const query = searchQuery.toLowerCase();
    return analyses.filter(a => 
      a.company_name?.toLowerCase().includes(query) ||
      a.document_name?.toLowerCase().includes(query) ||
      a.fiscal_years_display?.toLowerCase().includes(query) ||
      a.display_name?.toLowerCase().includes(query)
    );
  }, [analyses, searchQuery]);

  const handleSelect = (analysis) => {
    setSelected(prev => {
      const isAlreadySelected = prev.some(a => a.analysis_id === analysis.analysis_id);
      
      if (isAlreadySelected) {
        // Deselect
        return prev.filter(a => a.analysis_id !== analysis.analysis_id);
      } else if (prev.length < 2) {
        // Add to selection
        return [...prev, analysis];
      } else {
        // Replace second selection
        return [prev[0], analysis];
      }
    });
  };

  const getSelectionNumber = (analysis) => {
    const idx = selected.findIndex(a => a.analysis_id === analysis.analysis_id);
    return idx === -1 ? null : idx + 1;
  };

  const handleCompare = () => {
    if (selected.length === 2) {
      onCompare(selected[0].analysis_id, selected[1].analysis_id);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-surface-overlay backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-surface-page border border-default rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-subtle bg-gradient-to-r from-violet-500/10 via-transparent to-cyan-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center">
              <Scale size={20} className="text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-100">{t('compareAnalysesTitle')}</h2>
              <p className="text-sm text-slate-400">{t('compareDescription')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-primary transition-colors"
          >
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Search */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-subtle">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchCompanyYearDoc')}
              className="w-full bg-surface-secondary border border-subtle rounded-lg pl-10 pr-4 py-2.5 text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
            />
          </div>
        </div>

        {/* Selection Summary */}
        {selected.length > 0 && (
          <div className="flex-shrink-0 px-6 py-3 bg-slate-800/30 border-b border-subtle">
            <div className="flex items-center gap-4">
              <span className="text-xs text-slate-500 uppercase tracking-wide">{t('selected')}</span>
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                {selected[0] && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-500/20 border border-violet-500/30 rounded-lg">
                    <span className="w-4 h-4 rounded-full bg-violet-500 text-content text-[10px] font-bold flex items-center justify-center">1</span>
                    <span className="text-xs text-violet-300 truncate max-w-[180px]">
                      {selected[0].company_name}
                      {selected[0].fiscal_years_display && ` (${selected[0].fiscal_years_display})`}
                    </span>
                  </div>
                )}
                {selected.length === 2 && (
                  <>
                    <span className="text-slate-600">vs</span>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 border border-cyan-500/30 rounded-lg">
                      <span className="w-4 h-4 rounded-full bg-cyan-500 text-content text-[10px] font-bold flex items-center justify-center">2</span>
                      <span className="text-xs text-cyan-300 truncate max-w-[180px]">
                        {selected[1].company_name}
                        {selected[1].fiscal_years_display && ` (${selected[1].fiscal_years_display})`}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analysis List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredAnalyses.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={40} className="mx-auto text-slate-700 mb-3" />
              <p className="text-slate-400">{t('noAnalysesFound')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('uploadDocsToCreate')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredAnalyses.map((analysis) => {
                const selectionNumber = getSelectionNumber(analysis);
                const isSelected = selectionNumber !== null;
                
                return (
                  <AnalysisCard
                    key={analysis.analysis_id}
                    analysis={analysis}
                    isSelected={isSelected}
                    selectionNumber={selectionNumber}
                    onSelect={handleSelect}
                    disabled={false}
                    t={t}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-subtle bg-slate-900/80 flex items-center justify-between">
          <div className="text-sm text-slate-400">
            {selected.length === 0 && t('selectTwoAnalyses')}
            {selected.length === 1 && t('selectOneMore')}
            {selected.length === 2 && (
              <span className="flex items-center gap-2 text-emerald-400">
                <CheckCircle size={16} />
                {t('readyToCompare')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-400 hover:text-slate-300 transition-colors"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleCompare}
              disabled={selected.length !== 2 || isLoading}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                selected.length === 2 && !isLoading
                  ? 'bg-gradient-to-r from-violet-600 to-cyan-600 text-content-inverse hover:from-violet-500 hover:to-cyan-500 shadow-lg shadow-violet-500/20'
                  : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-strong border-t-content rounded-full animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  {t('compareAnalyses')}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonSelector;
